import type { ReportAggregation } from "@glorify-llc/accounting-core";

export interface MinistryVolunteerUtilizationRow {
  ministry_id: string;
  ministry_name: string;
  total_volunteer_slots: number;
  filled_slots: number;
  unfilled_slots: number;
  utilization_percent: number;
  unique_volunteers: number;
}

export type MinistryVolunteerUtilizationContext = {
  asOf: Date;
  ministries: MinistryVolunteerUtilizationRow[];
};

/**
 * Coerce a possibly-null/undefined/NaN/string-numeric value to a finite number.
 * PostgREST serialises NUMERIC columns as strings; without coercion a single
 * unparseable upstream value poisons aggregates (NaN propagates through `+`)
 * and `toFixed` throws on non-number inputs.
 */
function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Format a finite-number percent value with trailing "%". */
function formatPercent(value: number, decimals = 0): string {
  return `${toFiniteNumber(value).toFixed(decimals)}%`;
}

export function buildMinistryVolunteerUtilizationAggregation(
  context: MinistryVolunteerUtilizationContext,
): ReportAggregation {
  // Defensive: upstream payload could be null/undefined when the query returns
  // nothing (or before the first fetch resolves) — without a guard `.map` and
  // `.reduce` would TypeError.
  const ministries = Array.isArray(context.ministries) ? context.ministries : [];

  const reportRows = ministries.map((m) => {
    const id = typeof m.ministry_id === "string" && m.ministry_id.length > 0
      ? m.ministry_id
      : "unknown";
    const rawName = typeof m.ministry_name === "string" ? m.ministry_name.trim() : "";
    const totalSlots = toFiniteNumber(m.total_volunteer_slots);
    const filledSlots = toFiniteNumber(m.filled_slots);
    const unfilledSlots = toFiniteNumber(m.unfilled_slots);
    const uniqueVolunteers = toFiniteNumber(m.unique_volunteers);
    const utilizationPercent = toFiniteNumber(m.utilization_percent);

    return {
      id,
      label: rawName.length > 0 ? rawName : "Unknown Ministry",
      entityType: "ministry" as const,
      drilldownUrl: `/ministries/${encodeURIComponent(id)}`,
      totalSlots,
      filledSlots,
      unfilledSlots,
      utilization: formatPercent(utilizationPercent),
      utilizationPercent,
      uniqueVolunteers,
    };
  });

  // Stable, locale-independent sort: highest utilization-rate first (the
  // most-utilised ministries surface at the top), then by ministry name as a
  // deterministic tiebreaker, then by id so identical names still produce a
  // deterministic order across reloads. Use `<`/`>` rather than localeCompare
  // so the ordering is locale-independent (#8).
  reportRows.sort((a, b) => {
    const ar = a.utilizationPercent;
    const br = b.utilizationPercent;
    if (ar !== br) return br - ar;
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  });

  // Aggregate over coerced values so a single non-numeric row cannot poison
  // the totals via NaN propagation.
  let totalSlots = 0;
  let totalFilled = 0;
  for (const m of ministries) {
    totalSlots += toFiniteNumber(m.total_volunteer_slots);
    totalFilled += toFiniteNumber(m.filled_slots);
  }

  // Compute the overall utilization as a weighted ratio
  // (sum filled / sum slots), not as a mean of per-ministry percents. The
  // simple-average approach over-weights tiny ministries — a 2-slot ministry
  // at 100% would dominate a 1,000-slot ministry at 10% (sibling PR #1561
  // regression). Guard against divide-by-zero when no ministry has any slots.
  const overallUtilPercent = totalSlots > 0 ? (totalFilled / totalSlots) * 100 : 0;

  return {
    asOf: context.asOf,
    summary: [
      { label: "Ministries", value: ministries.length },
      { label: "Total Slots", value: totalSlots },
      { label: "Filled", value: totalFilled },
      { label: "Utilization", value: formatPercent(overallUtilPercent) },
    ],
    rows: reportRows,
  };
}
